import { setAudioModeAsync } from 'expo-audio';
import type { AudioRecorder } from 'expo-audio';
import * as FileSystem from 'expo-file-system/legacy';
import { useCallback, useState } from 'react';
import { useUploadTempFile } from '../../v2BaseComponents/ImagePicker/TempFilePicker/useTempFile';
import { Platform } from 'react-native';
import { base64EncodingFromUri } from '../../../utils/processSystemFile';
import { getIntl } from '../../../utils/intl';
import getDateString from '../../../utils/getDateString';
import { notifyBugsnag } from '../../../utils/bugsnag';

const intl = getIntl('useProcessAndUploadFile', {
    description: 'audio recording from {date}',
});

export type CreatedRecording = {
    tempFileId: string;
    description: string;
    name: string;
    contentType: string;
};

export type UseProcessAndUploadFileArgs = {
    onCreatedRecording: (recording: CreatedRecording) => void;
};

export type UseProcessAndUploadFileReturnValue = {
    isProcessingAndUploading: boolean;
    processAudio: (audio: AudioRecorder) => void;
    killAudio: (audio?: AudioRecorder | null) => Promise<void>;
};

const getBase64UriFromWebBlobUri = async (uri: string) => {
    // utility function to convert BLOB to BASE64
    const blobToBase64 = (blob: Blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return new Promise((resolve) => {
            reader.onloadend = () => {
                resolve(reader.result);
            };
        });
    };

    const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = function () {
            resolve(xhr.response as Blob);
        };
        xhr.onerror = function (_e) {
            reject(new TypeError('Network request failed'));
        };
        xhr.responseType = 'blob';
        xhr.open('GET', uri, true);
        xhr.send(null);
    });

    const audioBase64Uri = await blobToBase64(blob);

    // We're done with the blob and file uploading, close and release it
    (blob as any).close?.();

    return audioBase64Uri as string;
};

async function getURIAndBase64(uri: string): Promise<{ base64: string; uri: string }> {
    if (Platform.OS === 'web') {
        const base64Uri = await getBase64UriFromWebBlobUri(uri);
        return { base64: base64EncodingFromUri(base64Uri), uri: base64Uri };
    } else {
        const base64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
        return { base64, uri };
    }
}

export const useProcessAndUploadFile = ({
    onCreatedRecording,
}: UseProcessAndUploadFileArgs): UseProcessAndUploadFileReturnValue => {
    const uploadTempFile = useUploadTempFile();
    const [isProcessingAndUploading, setIsProcessingAndUploading] = useState<boolean>(false);

    const processAudio = async (audio: AudioRecorder) => {
        try {
            setIsProcessingAndUploading(true);
            await audio.stop();
            await setAudioModeAsync({
                allowsRecording: false,
            });
            const uri = audio.uri;
            if (!uri) {
                throw new Error('Failed to get audio URI');
            }

            const extension = Platform.OS === 'web' ? 'webm' : 'm4a';
            const name = `recording-${new Date().toISOString()}.${extension}`;
            const base64AndUri = await getURIAndBase64(uri);

            const uploadTempFileResult = await uploadTempFile({
                base64: base64AndUri.base64,
                originalName: name,
                uri: base64AndUri.uri,
            });
            if (!uploadTempFileResult) {
                throw new Error('Failed to upload audio');
            }

            onCreatedRecording({
                tempFileId: uploadTempFileResult.tempFileId,
                description: intl('description', { date: getDateString(new Date().toISOString(), 'date-time') }),
                name,
                contentType: uploadTempFileResult.contentType,
            });
        } catch (err) {
            notifyBugsnag(err as any);
            console.error('Failed to process audio', err);
        } finally {
            setIsProcessingAndUploading(false);
        }
    };

    const killAudio = useCallback(async (audio?: AudioRecorder | null) => {
        try {
            if (audio) {
                // Always stop, not just when isRecording — paused recordings keep the mic stream alive.
                await audio.stop();
            }
        } catch (err) {
            notifyBugsnag(err as any);
            console.error('Failed to kill audio', err);
        }
    }, []);

    return { isProcessingAndUploading, processAudio, killAudio };
};
